import { cva } from 'class-variance-authority';
import { Card, Flex, Text, Icon, IconButton, iconWeight } from '@nula/core-ui';
import { useNavigateToEntity } from '@/hooks/navigation';
import { useWorkspaceDoc } from '@/lib/sync/hooks/use-workspace-doc';
import { getEntityKindMeta } from '@/utils/entity-kind-meta';
import styles from './calendar-linked-note.module.css';

const linkedNote = cva(styles.inner);

interface CalendarLinkedNoteProps {
  documentId: string;
  color?: string;
  /** When provided, renders a remove button that unlinks this doc from the event. */
  onRemove?: () => void;
}

export function CalendarLinkedNote({ documentId, color, onRemove }: CalendarLinkedNoteProps) {
  const navigateToEntity = useNavigateToEntity();
  const doc = useWorkspaceDoc(documentId);

  // Doc was archived/deleted — don't render
  if (!doc || doc.isArchived) return null;

  const editedAt = new Date(doc.updatedAt);
  const now = new Date();
  const diffHours = Math.floor((now.getTime() - editedAt.getTime()) / (1000 * 60 * 60));
  const editedLabel = diffHours < 1
    ? 'Edited just now'
    : diffHours < 24
    ? `Edited ${diffHours}h ago`
    : `Edited ${Math.floor(diffHours / 24)}d ago`;

  return (
    <Card
      hoverable
      onClick={() => navigateToEntity(documentId)}
      className={styles.card}
    >
      <Flex vertical className={linkedNote()}>
        {/* Icon + title */}
        <Flex align="center" gap="md" className={styles.header}>
          <Flex
            align="center"
            justify="center"
            className={styles.iconWrap}
            style={{ '--doc-color': color ?? 'var(--primary)' } as React.CSSProperties}
          >
            <Icon
              name={getEntityKindMeta(doc.kind).icon}
              size={14}
              weight={iconWeight.light}
              className={styles.docIcon}
            />
          </Flex>
          <Text className={styles.title}>
            {doc.title || 'Untitled'}
          </Text>
          {onRemove && (
            <IconButton
              size="sm"
              aria-label="Remove link"
              className={styles.removeBtn}
              onClick={(e) => {
                // Stop the card's onClick (navigate-to-doc) from firing.
                e.stopPropagation();
                onRemove();
              }}
            >
              <Icon name="close" size={14} weight={iconWeight.light} />
            </IconButton>
          )}
        </Flex>

        {/* Snippet */}
        {doc.snippet && (
          <Text size="sm" className={styles.snippet}>
            {doc.snippet}
          </Text>
        )}

        {/* Edited label */}
        <Flex align="center" gap="xs" className={styles.editedRow}>
          <Icon name="edit" size={12} weight={iconWeight.light} className={styles.editedIcon} />
          <Text size="xs" className={styles.editedLabel}>
            {editedLabel}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
}
